import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { toast } from "sonner";
import { getCsrfToken } from "@/services/api.service";

let isLoggingOutDueToBlock = false;

// CSRF double-submit — backend exige header X-CSRF-Token em mutations (POST/PUT/PATCH/DELETE).
// `apiRequest` aqui é usada por chamadas via TanStack Query (ex: Tesouraria, async-export).
// Sem este header, CsrfGuard do super-app retorna 403 "CSRF validation failed".
const CSRF_HEADER = "X-CSRF-Token";
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;

    if (res.status === 403 && text.includes("conta foi bloqueada") && !isLoggingOutDueToBlock) {
      isLoggingOutDueToBlock = true;
      toast.error("Sua conta foi bloqueada. Você será desconectado.");
      window.location.href = "/login";
      setTimeout(() => {
        isLoggingOutDueToBlock = false;
      }, 5000);
    }

    throw new Error(`${res.status}: ${text}`);
  }
}

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

export async function apiRequest(method: string, url: string, data?: unknown | undefined): Promise<Response> {
  const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;

  const isFormData = data instanceof FormData;

  const headers: Record<string, string> = {
    ...(!isFormData && data ? { "Content-Type": "application/json" } : {}),
  };

  // Anexa header CSRF em mutations (mesmo padrão de ApiService.request)
  if (UNSAFE_METHODS.has(method.toUpperCase())) {
    const csrf = getCsrfToken();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }

  const res = await fetch(fullUrl, {
    method,
    headers,
    body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: { on401: UnauthorizedBehavior }) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }: { queryKey: readonly unknown[] }) => {
    const url = queryKey.join("/") as string;
    const fullUrl = url.startsWith("http") ? url : `${API_BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;

    const res = await fetch(fullUrl, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
